import { useEffect, useState } from 'react';
import { Export, PlusSquare, X } from '@phosphor-icons/react';
import { isIos, isStandalone } from '../platform';

/**
 * "Add to Home Screen". Android/desktop Chromium fire `beforeinstallprompt`,
 * which we hold on to and replay from an Install button. iOS has no install
 * API in any browser, so there we explain the two taps instead. Hidden once
 * running as an installed app.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'planesight:installDismissed';

// The install event fires once, often before React has mounted — capture it at module load.
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

export default function InstallPrompt({ variant }: { variant: 'card' | 'banner' }) {
  const [, rerender] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const l = () => rerender((n) => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);

  if (isStandalone()) return null;
  const ios = isIos();
  if (!deferred && !ios) return null; // browser can't install this (or already installed)
  if (variant === 'banner' && dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode */
    }
  };

  const install = async () => {
    const ev = deferred;
    if (!ev) return;
    deferred = null;
    await ev.prompt();
    const choice = await ev.userChoice;
    if (choice.outcome === 'dismissed') dismiss();
    rerender((n) => n + 1);
  };

  return (
    <div className={`install-prompt install-${variant}${variant === 'banner' ? ' glass' : ''}`} role="note">
      <img src="/apple-touch-icon.png" alt="" className="install-icon" />
      <div className="install-text">
        <strong>Add PlaneSight to your Home Screen</strong>
        {ios ? (
          <span>
            Tap{' '}
            <span className="install-glyph" aria-hidden="true">
              <Export size={14} weight="bold" />
            </span>{' '}
            Share, then{' '}
            <span className="install-glyph" aria-hidden="true">
              <PlusSquare size={14} weight="bold" />
            </span>{' '}
            Add to Home Screen. It opens full screen, like an app.
          </span>
        ) : (
          <span>Opens full screen, like an app.</span>
        )}
      </div>
      {!ios && (
        <button className="install-btn" onClick={() => void install()}>
          Install
        </button>
      )}
      {variant === 'banner' && (
        <button className="icon-btn sm" onClick={dismiss} aria-label="Dismiss" title="Dismiss">
          <X size={14} weight="bold" />
        </button>
      )}
    </div>
  );
}
